import { useRef, useCallback } from 'react';

/**
 * Custom hook for managing iframe scroll position restoration
 *
 * Handles:
 * - Saving scroll positions as user scrolls
 * - Restoring scroll positions when switching between iframes
 * - Updating shadow state based on scroll position
 *
 * @param {Function} updateShadow - Callback to update shadow state (receives scrollY)
 * @returns {Object} Object containing scroll management functions
 */
export const useIframeScrollRestoration = (updateShadow) => {
  const scrollPositions = useRef({});

  /**
   * Save scroll position for an iframe
   * @param {string} iframeId - The iframe element ID
   * @param {number} scrollY - The vertical scroll position
   */
  const saveScrollPosition = useCallback((iframeId, scrollY) => {
    console.log('[SAVE] iframeId:', iframeId, 'scrollY:', scrollY);
    scrollPositions.current[iframeId] = scrollY;
    console.log('[SAVE] All positions:', scrollPositions.current);
  }, []);

  /**
   * Restore scroll position for an iframe
   *
   * Uses requestAnimationFrame to ensure:
   * 1. React has finished current render cycle
   * 2. Display style changes have taken effect (display: none -> block)
   * 3. Browser has reflowed iframe layout
   * 4. Iframe contentWindow is accessible
   *
   * @param {string} iframeId - The iframe element ID
   * @param {boolean} isActiveIframe - Whether this iframe is currently visible
   * @returns {Promise<Object>} Result object with success status and scroll position
   */
  const restoreScrollPosition = useCallback((iframeId, isActiveIframe = false) => {
    console.log('[RESTORE] Called - iframeId:', iframeId, 'isActive:', isActiveIframe, 'all positions:', scrollPositions.current);
    return new Promise((resolve) => {
      // Use requestAnimationFrame to wait for layout to complete
      requestAnimationFrame(() => {
        const iframe = document.getElementById(iframeId);

        if (!iframe) {
          console.log('[RESTORE] FAILED - iframe not found:', iframeId);
          resolve({ success: false, reason: 'iframe_not_found' });
          return;
        }

        if (!iframe.contentWindow) {
          console.log('[RESTORE] FAILED - contentWindow not accessible:', iframeId);
          resolve({ success: false, reason: 'contentWindow_inaccessible' });
          return;
        }

        const savedPosition = scrollPositions.current[iframeId];
        console.log('[RESTORE] Saved position for', iframeId, ':', savedPosition);

        if (savedPosition === undefined) {
          console.log('[RESTORE] No saved position - this is first visit');
          // Not an error - first visit has no saved position
          // Update shadow to default state (hidden) if this is the active iframe
          if (isActiveIframe && updateShadow) {
            updateShadow(0);
          }
          resolve({ success: true, reason: 'no_saved_position', scrollY: 0 });
          return;
        }

        try {
          // Double-check contentWindow is still accessible
          // (may have changed between initial check and requestAnimationFrame callback)
          if (!iframe.contentWindow) {
            throw new Error('contentWindow became inaccessible');
          }

          console.log('[RESTORE] Attempting to scroll to:', savedPosition);
          iframe.contentWindow.scrollTo(0, savedPosition);
          console.log('[RESTORE] SUCCESS - scrolled to:', savedPosition);

          // Update shadow state if this is the active iframe
          if (isActiveIframe && updateShadow) {
            updateShadow(savedPosition);
          }

          resolve({ success: true, scrollY: savedPosition });
        } catch (error) {
          console.log('[RESTORE] FAILED - error:', error);
          // Reset shadow to default state on failure
          if (isActiveIframe && updateShadow) {
            updateShadow(0);
          }

          resolve({ success: false, reason: 'scroll_failed', error });
        }
      });
    });
  }, [updateShadow]);

  /**
   * Get saved scroll position for an iframe
   * @param {string} iframeId - The iframe element ID
   * @returns {number|undefined} The saved scroll position or undefined
   */
  const getScrollPosition = useCallback((iframeId) => {
    return scrollPositions.current[iframeId];
  }, []);

  return {
    saveScrollPosition,
    restoreScrollPosition,
    getScrollPosition,
    scrollPositions // Expose ref for direct access if needed
  };
};
